import { AxiosProgressEvent } from 'axios';
import {
  ChatRequest,
  CreateContractRequest,
  GetDocumentsListRequest,
  GetEvaluationInsightsRequest,
  UpdateContractRequest,
  CreateContractForProjectRequest,
} from '../types/Dtos/request';
import { CreateContractResponse, GetContractDetailsResponse } from '../types/Dtos/response';
import { api } from '../utils/api';
import { DocumentTypeEnum, ProxyEvaluationTypeEnum } from '../utils/constants';
import {
  deleteDocuments,
  getIndexingStatus,
  listDocuments,
  runAnalysis,
  uploadSingleDocument,
} from './documentsService';
import { connectToChatStream } from './chatService';
import {
  createEvaluation,
  deleteEvaluation,
  getEvaluationDetails,
  getEvaluationInsights,
  starEvaluationStatement,
  unstarEvaluationStatement,
  updateEvaluation,
} from './evaluationsService';

const RESOURCE_TYPE = DocumentTypeEnum.CONTRACT; // 30

export const uploadContractDocument = (
  file: File,
  contractId: string,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
) =>
  uploadSingleDocument(
    file,
    ProxyEvaluationTypeEnum.contract,
    contractId,
    undefined,
    onUploadProgress,
  );

export const connectToContractChatStream = ({
  id,
  conversationId,
  userPrompt,
  specifiedDocuments,
  clarificationResponse,
}: ChatRequest) => {
  const body = {
    chat_session_id: `${conversationId}`,
    eval_id: id,
    eval_type: ProxyEvaluationTypeEnum.contract,
    query: userPrompt,
    specified_documents: specifiedDocuments,
    clarification_response: undefined,
  };
  if (clarificationResponse) body.clarification_response = clarificationResponse;
  return connectToChatStream(id, ProxyEvaluationTypeEnum.contract, body, true);
};

export const getContractDetails = (contractId: string) =>
  getEvaluationDetails(contractId) as Promise<
    import('axios').AxiosResponse<GetContractDetailsResponse>
  >;

export const analyseContractDocuments = (contractId: string) =>
  runAnalysis(ProxyEvaluationTypeEnum.contract, contractId);

export const createContractForProject = ({
  payload,
  organizationId,
  projectId,
}: CreateContractForProjectRequest) =>
  createEvaluation(organizationId, {
    ...payload,
    projectId,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateContractResponse>>;

export const createContractForOrganization = (
  payload: CreateContractRequest,
  organizationId: string,
) =>
  createEvaluation(organizationId, {
    ...payload,
    resourceType: RESOURCE_TYPE,
  }) as Promise<import('axios').AxiosResponse<CreateContractResponse>>;

export const updateContract = (payload: UpdateContractRequest, contractId: string) =>
  updateEvaluation(contractId, { ...payload }) as Promise<
    import('axios').AxiosResponse<CreateContractResponse>
  >;

export const deleteContract = (contractId: string) => deleteEvaluation(contractId);

export const exportContractReport = (contractId: string) =>
  api.get<Blob>(`/contracts/${contractId}/overview/export`, {
    responseType: 'blob',
  });

export const deleteContractDocuments = (contractId: string, documentsIds: string[]) =>
  deleteDocuments(ProxyEvaluationTypeEnum.contract, contractId, documentsIds);

export const markContractStatementAsStarred = (contractId: string, statementId: string) =>
  starEvaluationStatement(contractId, statementId);

export const unmarkContractStatementAsStarred = (contractId: string, statementId: string) =>
  unstarEvaluationStatement(contractId, statementId);

export const getContractDocuments = ({
  id,
  orderBy,
  orderDirection,
  searchText,
}: GetDocumentsListRequest) =>
  listDocuments(ProxyEvaluationTypeEnum.contract, id, orderBy, orderDirection, searchText);

export const getContractInsights = ({ evaluationId, ...payload }: GetEvaluationInsightsRequest) =>
  getEvaluationInsights({ evaluationId, ...payload });

export const getProxyContractIndexedDocumentsStatus = (contractId: string) =>
  getIndexingStatus(ProxyEvaluationTypeEnum.contract, contractId);
